import React, {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import Select from 'react-select'
import Swal from 'sweetalert2'

import {
  fetchCommissionConfigs,
  fetchCustomers,
  downloadCommissionConfigsCsv,
} from './apis/requestsApi'
import {CommissionCfgRow, CustomerItem} from './core/CommissionConfigs.model'
import Pagination from './Pagination'                // ⬅️ re‑usa tu paginador

/* ------------------------------------------------------------------ */
/*  Tipos                                                              */
/* ------------------------------------------------------------------ */
interface CustomerOption {
  value: number | null
  label: string
}

/* ------------------------------------------------------------------ */
/*  Componente                                                         */
/* ------------------------------------------------------------------ */
export const CommissionConfigsList: React.FC = () => {
  /* ------------- estados de datos ------------------- */
  const [flatRows, setFlatRows]         = useState<CommissionCfgRow[]>([])
  const [techPartners, setTechPartners] = useState<string[]>([])
  const [page, setPage]                 = useState(1)
  const [limit, setLimit]               = useState(10)
  const [totalPages, setTotalPages]     = useState(0)

  /* ------------- estados de filtro ------------------ */
  const [customers, setCustomers]               = useState<CustomerOption[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption>({
    value: null,
    label: 'Todos',
  })

  /* ------------- estado ui -------------------------- */
  const [loading, setLoading] = useState(false)

  /* ----------------------------------------------------------------*/
  /* Helpers                                                         */
  /* ----------------------------------------------------------------*/
  const fmtPct = (str: string) =>
    (isNaN(+str) ? 0 : +str).toFixed(2).replace('.', ',') + ' %'

  /* ----------------------------------------------------------------*/
  /* 1) Cargar opciones de clientes                                  */
  /* ----------------------------------------------------------------*/
  useEffect(() => {
    fetchCustomers()
      .then((raw) => {
        const opts = raw.map(
          (c: CustomerItem): CustomerOption => ({value: c.id, label: c.name})
        )
        setCustomers([{value: null, label: 'Todos'}, ...opts])
      })
      .catch(() =>
        Swal.fire('Error', 'No se pudo cargar la lista de clientes', 'error')
      )
  }, [])

  /* ----------------------------------------------------------------*/
  /* 2) Cargar tabla al cambiar filtros / paginación                 */
  /* ----------------------------------------------------------------*/
  useEffect(() => {
    const custId = selectedCustomer.value ?? undefined
    setLoading(true)
    fetchCommissionConfigs(page, limit, custId)
      .then(({rows, totalPages, techPartners}) => {
        setFlatRows(rows)
        setTotalPages(totalPages)
        setTechPartners(techPartners)
      })
      .catch(() =>
        Swal.fire('Error', 'No se pudo cargar la información', 'error')
      )
      .finally(() => setLoading(false))
  }, [page, limit, selectedCustomer])

  /* ----------------------------------------------------------------*/
  /* 3) Agrupar filas por cliente                                    */
  /* ----------------------------------------------------------------*/
  const grouped = useMemo(() => {
    type Group = {customerName: string; byTp: Record<string, CommissionCfgRow>}
    const m: Record<number, Group> = {}
    for (const r of flatRows) {
      if (!m[r.customerId]) m[r.customerId] = {customerName: r.customerName, byTp: {}}
      m[r.customerId].byTp[r.techPartner] = r
    }
    return Object.values(m)
  }, [flatRows])

  /* ----------------------------------------------------------------*/
  /* 4) Descargar CSV                                                */
  /* ----------------------------------------------------------------*/
  const handleDownload = async () => {
    try {
      const custId = selectedCustomer.value ?? undefined
      const blob   = await downloadCommissionConfigsCsv(page, limit, custId)
      const url    = window.URL.createObjectURL(blob)
      const link   = Object.assign(document.createElement('a'), {
        href: url,
        download: 'commission_configs.csv',
      })
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      Swal.fire('Error', 'No se pudo descargar el CSV', 'error')
    }
  }

  /* ====================================================================== */
  /*  Render                                                                */
  /* ====================================================================== */
  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-body py-3'>

        {/* ------------------------ Header & Botones --------------------- */}
        <div className='d-flex flex-wrap align-items-center justify-content-between mb-5'>
          <h3 className='mb-3 mb-md-0'>Comisiones de los Clientes</h3>

          <div className='split-button'>
            <Link
              to='/products/client-commission'
              className='btn btn-secondary rounded-pill me-3'
              aria-disabled={loading}
            >
              Comisión personalizada
            </Link>

            <button
              className='btn btn-primary rounded-pill'
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? 'Procesando…' : 'Descargar CSV'}
            </button>
          </div>
        </div>

        {/* ------------------------ Filtro Cliente ----------------------- */}
        <div className='row flex-wrap mb-5'>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <label className='form-label'>Cliente</label>
            <Select<CustomerOption>
              options={customers}
              value={selectedCustomer}
              onChange={(opt) => {
                setPage(1)
                setSelectedCustomer(opt ?? {value: null, label: 'Todos'})
              }}
              isClearable={false}
              isDisabled={loading}
              classNamePrefix='react-select'
            />
          </div>
        </div>

        {/* ------------------------ Tabla ------------------------------- */}
        <div className='table-responsive mb-20'>
          {loading ? (
            <p className='mb-0'><strong>Cargando…</strong></p>
          ) : grouped.length === 0 ? (
            <p className='mb-0'><strong>No hay datos disponibles</strong></p>
          ) : (
            <table className='table table-striped'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200'>
                  <th className='min-w-200px'>Cliente</th>
                  {techPartners.map((tp) => (
                    <React.Fragment key={tp}>
                      <th className='min-w-150px'>CST&nbsp;{tp}</th>
                      <th className='min-w-150px'>CSC&nbsp;{tp}</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>

              <tbody>
                {grouped.map((grp, i) => (
                  <tr key={i}>
                    <td>{grp.customerName}</td>
                    {techPartners.map((tp) => {
                      const cell    = grp.byTp[tp]
                      const pctTech = fmtPct(cell?.pctBaseTech ?? '0.00')
                      const pctComm = fmtPct(cell?.pctBaseCommercial ?? '0.00')
                      return (
                        <React.Fragment key={tp}>
                          <td>{pctTech}</td>
                          <td>{pctComm}</td>
                        </React.Fragment>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ------------------------ Paginador --------------------------- */}
        {grouped.length > 0 && (
          <div className='d-flex justify-content-between align-items-center'>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value))
                setPage(1)
              }}
              className='form-select form-select-sm w-auto'
            >
              {[10, 15, 30, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <Pagination
              currentPage={page}
              setCurrentPage={setPage}
              totalPages={totalPages}
              limit={limit}
              lengthData={grouped.length}
            />
          </div>
        )}
      </div>
    </div>
  )
}
